/** Admin API methods for auctions, backorders, VNNIC corrections, RDAP, RDE, cases and legal locks (subsystems/registrar/facade/adapters/admin-api/src/admin/market.rs). */

import type { CallOptions, DecimalString, ISODateTime, ItemList, RecordedReason, VnnicSyncErrorCode, VnnicSyncStatus, VnnicTransactionStatus, WriteOptions } from '../types';

// ---------------------------------------------------------------------------
// Auctions, backorders, drops (subsystems/registrar/facade/adapters/admin-api/src/admin/auctions.rs).
//
// These endpoints return database rows as `to_jsonb(row)`, with NUMERIC money columns
// (prices, amounts) as exact decimal strings. Every amount travels with its `currency`.

export type AuctionKind = 'PUBLIC' | 'BACKORDER_PRIVATE';
export type AuctionStatus = 'SCHEDULED' | 'OPEN' | 'CLOSED' | 'SETTLING' | 'SETTLED' | 'UNSOLD' | 'FAILED' | 'CANCELLED';

/** `auction.auctions` row without `policy_sha256`. */
export interface Auction {
  id: string;
  kind: AuctionKind;
  domain_name: string;
  tld_id: string;
  drop_id: string | null;
  status: AuctionStatus;
  currency: string;
  start_price: DecimalString;
  reserve_price: DecimalString | null;
  current_price: DecimalString | null;
  highest_bid_id: string | null;
  highest_bidder_id: string | null;
  /** Display name of the leading bidder (joined in; null when there is no bid yet). */
  leader_name: string | null;
  leader_hold_id: string | null;
  bid_count: number;
  event_count: number;
  starts_at: ISODateTime;
  ends_at: ISODateTime;
  original_ends_at: ISODateTime;
  extension_count: number;
  policy_key: string;
  policy_version: string;
  winner_bid_id: string | null;
  winner_customer_id: string | null;
  /** Display name of the winning customer (joined in). */
  winner_name: string | null;
  winner_hold_id: string | null;
  final_price: DecimalString | null;
  closed_at: ISODateTime | null;
  settled_at: ISODateTime | null;
  order_id: string | null;
  order_item_id: string | null;
  domain_id: string | null;
  capture_entry_id: string | null;
  override_approval_id: string | null;
  status_reason: string | null;
  created_by_type: string;
  created_by_id: string;
  version: number;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export interface AuctionListQuery {
  status?: AuctionStatus;
  kind?: AuctionKind;
  domain?: string;
  limit?: number;
}

export interface AuctionParticipant {
  auction_id: string;
  customer_id: string;
  /** Customer display name (joined in). */
  customer_name: string | null;
  subject_id: string;
  /** Registrant's legal name (joined in from the subject's current version). */
  subject_name: string | null;
  backorder_id: string | null;
  nameservers: string[];
  accepted_agreement_version: string | null;
  joined_at: ISODateTime;
}

export type BidRejectReason = 'AUCTION_NOT_OPEN' | 'AUCTION_ENDED' | 'BELOW_MINIMUM' | 'CURRENCY_MISMATCH' | 'NOT_ELIGIBLE' | 'INSUFFICIENT_FUNDS';

/** `auction.bids` row without `request_hash`. */
export interface AuctionBid {
  id: string;
  auction_id: string;
  sequence: number | null;
  bidder_customer_id: string;
  /** Bidder's customer display name (joined in). */
  bidder_name: string | null;
  amount: DecimalString;
  currency: string;
  status: 'ACCEPTED' | 'REJECTED';
  reject_reason: BidRejectReason | null;
  minimum_amount: DecimalString | null;
  client_bid_id: string;
  hold_id: string | null;
  received_at: ISODateTime;
  decided_at: ISODateTime;
  actor_type: string;
  actor_id: string;
  request_id: string | null;
  correlation_id: string | null;
}

export type AuctionEventType =
  | 'AUCTION_CREATED'
  | 'AUCTION_OPENED'
  | 'BID_ACCEPTED'
  | 'BID_REJECTED'
  | 'BIDDER_OUTBID'
  | 'AUCTION_EXTENDED'
  | 'AUCTION_CLOSED'
  | 'WINNER_SELECTED'
  | 'AUCTION_UNSOLD'
  | 'SETTLEMENT_STARTED'
  | 'PAYMENT_SETTLED'
  | 'DOMAIN_ASSIGNED'
  | 'SETTLEMENT_FAILED'
  | 'BACKORDER_ASSIGNED'
  | 'RESULT_OVERRIDDEN'
  | 'AUCTION_CANCELLED';

export interface AuctionEvent {
  id: string;
  auction_id: string;
  sequence: number;
  event_type: AuctionEventType;
  bid_id: string | null;
  payload: Record<string, unknown>;
  actor_type: string;
  actor_id: string;
  /** Display name of the staff user behind the event; null for workers and customers. */
  actor_name: string | null;
  request_id: string | null;
  correlation_id: string | null;
  occurred_at: ISODateTime;
}

/** `GET /auctions/{id}`: the full row (including `policy_sha256`), participants, every bid and the event history. */
export interface AuctionDetail extends Auction {
  policy_sha256: string;
  /** Display name of the staff user who created the auction. */
  created_by_name: string | null;
  participants: AuctionParticipant[];
  bids: AuctionBid[];
  events: AuctionEvent[];
}

export interface AuctionReasonRequest {
  reason: string;
}

export interface AuctionOverrideRequest {
  approval_request_id: string;
  reason: string;
}

export interface AuctionStatusChange {
  id: string;
  status: AuctionStatus;
}

export interface AuctionOverrideResult extends AuctionStatusChange {
  winner_bid_id: string | null;
  winner_customer_id: string | null;
  /** Decimal string (normalised). */
  final_price: string | null;
}

/** Four-eyes approval payload for `auction.result.override` (crates/auction `OverrideAction`). */
export type AuctionOverrideAction = { action: 'VOID' } | { action: 'AWARD'; bid_id: string };

export const AUCTION_OVERRIDE_ACTION = 'auction.result.override';
export const AUCTION_APPROVAL_RESOURCE = 'auction';

export type BackorderStatus = 'ACTIVE' | 'ASSIGNED' | 'IN_AUCTION' | 'WON' | 'LOST' | 'REGISTERING' | 'CAUGHT' | 'NOT_CAUGHT' | 'CANCELLED';

/** `auction.backorders` row without `policy_sha256`. */
export interface Backorder {
  id: string;
  customer_id: string;
  /** Customer display name (joined in). */
  customer_name: string | null;
  subject_id: string;
  domain_name: string;
  tld_id: string;
  registry_id: string;
  /** Registry name, e.g. "VNNIC" (joined in). */
  registry_name: string | null;
  level: string;
  priority_rank: number;
  price: DecimalString;
  currency: string;
  hold_id: string;
  nameservers: string[];
  accepted_agreement_version: string | null;
  status: BackorderStatus;
  status_reason: string | null;
  drop_id: string | null;
  auction_id: string | null;
  policy_key: string;
  policy_version: string;
  idempotency_key: string;
  version: number;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export type DropStatus = 'SCHEDULED' | 'AUCTION' | 'ASSIGNED' | 'CAUGHT' | 'NOT_CAUGHT' | 'NO_DEMAND' | 'CANCELLED';
export type DropSource = 'OPERATOR' | 'REGISTRY_NOTICE' | 'LIFECYCLE';
export type DropCatchStatus = 'PLANNED' | 'POLLING' | 'REGISTERING' | 'CAUGHT' | 'NOT_CAUGHT' | 'MANUAL_REVIEW';

/** `auction.drop_catch_attempts` row. */
export interface DropCatchAttempt {
  id: string;
  drop_id: string;
  backorder_id: string;
  auction_id: string | null;
  customer_id: string;
  /** Customer the name is being caught for, by display name (joined in). */
  customer_name: string | null;
  domain_name: string;
  registry_id: string;
  amount: DecimalString;
  currency: string;
  hold_id: string;
  status: DropCatchStatus;
  window_start: ISODateTime;
  window_end: ISODateTime;
  poll_interval_ms: number;
  next_check_at: ISODateTime;
  checks_sent: number;
  last_check_result: string | null;
  order_id: string | null;
  order_item_id: string | null;
  domain_id: string | null;
  workflow_id: string | null;
  capture_entry_id: string | null;
  outcome_reason: string | null;
  locked_by: string | null;
  locked_until: ISODateTime | null;
  completed_at: ISODateTime | null;
  version: number;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

/** `auction.drops` row with its drop-catch attempt. */
export interface Drop {
  id: string;
  domain_name: string;
  tld_id: string;
  registry_id: string;
  /** Registry name (joined in). */
  registry_name: string | null;
  expected_drop_at: ISODateTime;
  source: DropSource;
  source_reference: string | null;
  status: DropStatus;
  auction_id: string | null;
  status_reason: string | null;
  created_by_type: string;
  created_by_id: string;
  version: number;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  attempt: DropCatchAttempt | null;
}

/** `POST /auctions`: a PUBLIC auction under the rules in force (NewAuctionBody in auctions.rs). */
export interface NewAuctionRequest {
  domain: string;
  currency: string;
  /** Decimal string. */
  start_price: string;
  reserve_price?: string | null;
  /** Omitted: starts now. */
  starts_at?: ISODateTime | null;
  ends_at: ISODateTime;
}

/** `POST /auctions/drops`: an expected drop entered by staff (DropBody in auctions.rs). */
export interface NewDropRequest {
  domain: string;
  expected_drop_at: ISODateTime;
  source: DropSource;
  source_reference?: string | null;
}

export interface BackorderListQuery {
  status?: string;
  domain?: string;
  limit?: number;
}

/** The auction a backorder or drop went to, summarised with its winner's name. */
export interface AuctionRef {
  id: string;
  kind: AuctionKind;
  domain_name: string;
  status: AuctionStatus;
  currency: string;
  final_price: DecimalString | null;
  winner_customer_id: string | null;
  winner_name: string | null;
  bid_count: number;
  starts_at: ISODateTime;
  ends_at: ISODateTime;
}

/** The drop a backorder is queued for, summarised. */
export interface DropRef {
  id: string;
  domain_name: string;
  status: DropStatus;
  expected_drop_at: ISODateTime;
  source: DropSource;
  source_reference: string | null;
}

/** Funds reserved for a backorder (`billing.balance_holds`). */
export interface BackorderHold {
  id: string;
  amount: DecimalString;
  currency: string;
  purpose: string;
  status: 'ACTIVE' | 'RELEASED' | 'CAPTURED' | 'EXPIRED';
  expires_at: ISODateTime | null;
  created_at: ISODateTime;
  closed_at: ISODateTime | null;
}

export type BackorderEventType =
  | 'BACKORDER_PLACED'
  | 'BACKORDER_CANCELLED'
  | 'BACKORDER_ASSIGNED'
  | 'BACKORDER_IN_AUCTION'
  | 'BACKORDER_RETURNED'
  | 'BACKORDER_WON'
  | 'BACKORDER_LOST'
  | 'BACKORDER_REGISTERING'
  | 'BACKORDER_CAUGHT'
  | 'BACKORDER_NOT_CAUGHT';

/** `auction.backorder_events` row: the append-only history of one backorder. */
export interface BackorderEvent {
  id: string;
  backorder_id: string;
  event_type: BackorderEventType;
  payload: Record<string, unknown>;
  actor_type: string;
  actor_id: string;
  /** Display name of the staff user behind the event; null for workers and customers. */
  actor_name: string | null;
  request_id: string | null;
  correlation_id: string | null;
  occurred_at: ISODateTime;
}

/** `GET /auctions/backorders/{id}`: one backorder with its queue place, hold, drop, auction, attempt and history. */
export interface BackorderDetail extends Backorder {
  /** Registrant's legal name (joined in from the subject's current version). */
  subject_name: string | null;
  hold: BackorderHold;
  auction: AuctionRef | null;
  drop: DropRef | null;
  attempt: DropCatchAttempt | null;
  /** Backorders still competing for the name, and this one's place among them. */
  queue_size: number;
  queue_position: number;
  events: BackorderEvent[];
}

/** A backorder queued for a drop. */
export interface DropBackorder {
  id: string;
  customer_id: string;
  customer_name: string | null;
  level: string;
  priority_rank: number;
  price: DecimalString;
  currency: string;
  status: BackorderStatus;
  status_reason: string | null;
  created_at: ISODateTime;
}

/** `GET /auctions/drops/{id}`: one expected drop with its attempt, auction and queued backorders. */
export interface DropDetail extends Drop {
  /** Display name of the staff user who recorded the drop; null when a worker recorded it. */
  created_by_name: string | null;
  auction: AuctionRef | null;
  /** Highest priority first. */
  backorders: DropBackorder[];
}

// ---------------------------------------------------------------------------
// RDE (market.rs: /rde/deposits)

export type DepositStatus =
  | 'PENDING'
  | 'GENERATED'
  | 'VALIDATED'
  | 'VALIDATION_FAILED'
  | 'ENCRYPTED'
  | 'DELIVERING'
  | 'DELIVERED'
  | 'DELIVERY_FAILED'
  | 'DELIVERY_UNKNOWN'
  | 'CONFIRMED'
  | 'VERIFICATION_FAILED'
  | 'ABANDONED';
export type DepositType = 'FULL' | 'INCREMENTAL';
export type DepositFormatStatus = 'UNOFFICIAL_DRAFT' | 'OFFICIAL';
export type EscrowVerificationStatus = 'RECEIVED' | 'VERIFIED' | 'VERIFICATION_FAILED';

export interface EscrowDeposit {
  id: string;
  deposit_type: DepositType;
  watermark: ISODateTime;
  since_watermark: ISODateTime | null;
  base_deposit_id: string | null;
  policy_key: string;
  policy_version: string;
  format: string;
  format_status: DepositFormatStatus;
  scope: string;
  status: DepositStatus;
  record_count: number | null;
  deletion_count: number | null;
  checksum: string | null;
  encryption: string | null;
  agent: string | null;
  delivery_attempts: number;
  next_attempt_at: ISODateTime | null;
  external_reference: string | null;
  last_error_code: string | null;
  created_at: ISODateTime;
  generated_at: ISODateTime | null;
  validated_at: ISODateTime | null;
  delivered_at: ISODateTime | null;
  confirmed_at: ISODateTime | null;
  status_changed_at: ISODateTime;
  content_files: number;
  content_bytes: number;
  delivery_files: number;
  delivery_bytes: number;
  checks_passed: number;
  checks_failed: number;
  /** Latest receipt's verification status, if any. */
  verification_status: EscrowVerificationStatus | null;
}

/** A policy version as summarised by the admin API. */
export interface PolicyRef {
  policy_key: string;
  version: string;
  kind: string;
  status: string;
  authority: string;
  effective_from: ISODateTime;
  effective_until: ISODateTime | null;
  source_reference: string | null;
  notes: string[];
}

export interface EscrowPolicy extends PolicyRef {
  parameters: Record<string, unknown> | null;
}

export interface EscrowDepositList extends ItemList<EscrowDeposit> {
  /** Keyset cursor for the next page, or null on the last page. */
  next: { before: ISODateTime; before_type: DepositType } | null;
  /** ICANN_REGISTRAR_DATA_ESCROW in force now; null when none (the pipeline plans nothing). */
  policy: EscrowPolicy | null;
}

export interface EscrowDepositQuery {
  status?: DepositStatus;
  deposit_type?: DepositType;
  before?: ISODateTime;
  before_type?: DepositType;
  limit?: number;
}

export interface EscrowFile {
  id: string;
  role: 'CONTENT' | 'DELIVERY';
  name: string;
  sha256: string;
  size_bytes: number;
  encrypted: boolean;
  encryption: string | null;
  source_file_id: string | null;
  created_at: ISODateTime;
}

export interface EscrowValidationResult {
  id: string;
  file_id: string | null;
  file_name: string | null;
  validator: string;
  check_name: string;
  outcome: 'PASS' | 'FAIL';
  detail: Record<string, unknown>;
  created_at: ISODateTime;
}

export interface EscrowDeliveryAttempt {
  id: string;
  attempt: number;
  agent: string;
  status: 'SENDING' | 'DELIVERED' | 'FAILED' | 'UNKNOWN';
  external_reference: string | null;
  error_code: string | null;
  error_message: string | null;
  correlation_id: string | null;
  sent_at: ISODateTime;
  completed_at: ISODateTime | null;
}

export interface EscrowReceipt {
  id: string;
  attempt_id: string | null;
  source: 'AGENT' | 'OPERATOR';
  agent: string;
  agent_reference: string | null;
  verification_status: EscrowVerificationStatus;
  evidence_object_key: string | null;
  evidence_sha256: string | null;
  note: string | null;
  recorded_by_type: string;
  recorded_by_id: string;
  /** Staff name of whoever recorded the receipt; null for the escrow worker. */
  recorded_by_display_name?: string | null;
  recorded_at: ISODateTime;
}

/** `GET /rde/deposits/{id}`: the `escrow.deposits` row (without `policy_sha256`) and its children. */
export interface EscrowDepositDetail {
  id: string;
  deposit_type: DepositType;
  watermark: ISODateTime;
  since_watermark: ISODateTime | null;
  base_deposit_id: string | null;
  policy_key: string;
  policy_version: string;
  format: string;
  format_status: DepositFormatStatus;
  scope: string;
  status: DepositStatus;
  record_count: number | null;
  deletion_count: number | null;
  checksum: string | null;
  encryption: string | null;
  agent: string | null;
  delivery_attempts: number;
  next_attempt_at: ISODateTime | null;
  external_reference: string | null;
  last_error_code: string | null;
  last_error_message: string | null;
  created_by: string;
  /** Staff name of whoever started the deposit; null when the escrow worker did. */
  created_by_display_name: string | null;
  created_at: ISODateTime;
  generated_at: ISODateTime | null;
  validated_at: ISODateTime | null;
  encrypted_at: ISODateTime | null;
  delivered_at: ISODateTime | null;
  confirmed_at: ISODateTime | null;
  status_changed_at: ISODateTime;
  version: number;
  updated_at: ISODateTime;
  files: EscrowFile[];
  validation_results: EscrowValidationResult[];
  attempts: EscrowDeliveryAttempt[];
  receipts: EscrowReceipt[];
}

// ---------------------------------------------------------------------------
// Legal locks (market.rs: /legal-locks)

export type LockType = 'REGISTRAR_LOCK' | 'TRANSFER_LOCK' | 'UDRP_LOCK' | 'COURT_LOCK' | 'REGISTRY_LOCK' | 'COMPLIANCE_LOCK';
export const LEGAL_LOCK_TYPES: readonly LockType[] = ['UDRP_LOCK', 'COURT_LOCK', 'REGISTRY_LOCK', 'COMPLIANCE_LOCK'];
export const ALL_LOCK_TYPES: readonly LockType[] = ['REGISTRAR_LOCK', 'TRANSFER_LOCK', ...LEGAL_LOCK_TYPES];

export interface LegalLock {
  id: string;
  domain_id: string;
  domain_name: string;
  unicode_name: string;
  lifecycle_state: string;
  lock_type: LockType;
  reason: RecordedReason;
  case_reference: string | null;
  applied_by_actor_type: string;
  applied_by_actor_id: string;
  /** Staff name of whoever applied the lock; null for a customer or system actor. */
  applied_by_display_name: string | null;
  applied_at: ISODateTime;
  released_by_actor_type: string | null;
  released_by_actor_id: string | null;
  /** Staff name of whoever released the lock, never an e-mail. */
  released_by_display_name: string | null;
  released_at: ISODateTime | null;
  release_reason: RecordedReason | null;
  approval_request_id: string | null;
  active: boolean;
  dispute: { id: string; dispute_type: string; provider: string; case_reference: string; status: string } | null;
  abuse_case: { id: string; case_number: string; status: string; action_id: string; action: string } | null;
}

/** What each lock type blocks under the `REGISTRAR_LOCK_EFFECTS` version in force. */
export type LockEffects = { policy_key: string; version: string; blocks: Partial<Record<LockType, string[]>> } | null;

export interface LegalLockList extends ItemList<LegalLock> {
  lock_types: LockType[];
  /** Null when no policy is in force (every lock then blocks everything). */
  effects: LockEffects;
}

/** `GET /legal-locks/{id}`: one lock, the other locks on the same name, and what it blocks. */
export interface LegalLockDetail {
  lock: LegalLock;
  other_locks: LegalLock[];
  /** Statuses the registry itself reports for the name (e.g. `serverTransferProhibited`). */
  registry_statuses: string[];
  effects: LockEffects;
}

export interface LegalLockQuery {
  state?: 'active' | 'released' | 'all';
  lock_type?: LockType;
  domain?: string;
  limit?: number;
}

// ---------------------------------------------------------------------------
// Case inbox (market.rs: /cases/inbox)

export type CaseKind = 'ABUSE' | 'DISPUTE' | 'DISCLOSURE';

export interface InboxCase {
  kind: CaseKind;
  id: string;
  /** Abuse / disclosure case number, or the dispute's external case reference. */
  reference: string;
  domain_name: string;
  domain_id: string | null;
  status: string;
  open: boolean;
  /** Abuse category, dispute type or disclosure requester type. */
  subtype: string;
  /** Abuse cases only. */
  priority: string | null;
  /** Abuse source or dispute provider. */
  origin: string | null;
  opened_at: ISODateTime;
  updated_at: ISODateTime;
  /** First-response due (abuse) or disclosure deadline; disputes have none. */
  due_at: ISODateTime | null;
  overdue: boolean;
  /** No SLA / deadline policy was in force when the case was opened. */
  policy_missing: boolean;
}

export interface CaseInbox extends ItemList<InboxCase> {
  kinds_included: CaseKind[];
  /** Kinds asked for that the caller may not read. */
  kinds_forbidden: CaseKind[];
}

export interface CaseInboxQuery {
  kind?: CaseKind;
  include_closed?: boolean;
  domain?: string;
  limit?: number;
}

// ---------------------------------------------------------------------------
// VNNIC corrections (market.rs: /vnnic/corrections)

export interface VnnicCorrectionSync {
  dossier_id: string;
  domain_id: string;
  domain: string;
  dossier_version: number;
  dossier_status: string;
  operation: 'SUBMIT' | 'UPDATE';
  status: Extract<VnnicSyncStatus, 'REJECTED' | 'CORRECTION_REQUIRED' | 'RESUBMIT_PENDING'>;
  attempts: number;
  external_reference: string | null;
  last_error_code: VnnicSyncErrorCode | null;
  next_attempt_at: ISODateTime;
  status_changed_at: ISODateTime;
  last_transaction: {
    id: string;
    attempt: number;
    operation: string;
    status: VnnicTransactionStatus;
    adapter: string;
    error_code: VnnicSyncErrorCode | null;
    error_message: string | null;
    submitted_at: ISODateTime;
    completed_at: ISODateTime | null;
  } | null;
}

export interface VnnicCorrectionReport {
  id: string;
  report_type: string;
  period_start: ISODateTime;
  period_end: ISODateTime;
  revision: number;
  supersedes_batch_id: string | null;
  superseded_by_batch_id: string | null;
  status: string;
  schema_version: string;
  record_count: number;
  invalid_count: number;
  rejected_items: number;
  external_reference: string | null;
  generated_at: ISODateTime;
  submitted_at: ISODateTime | null;
  updated_at: ISODateTime;
}

export interface VnnicCorrections {
  sync: VnnicCorrectionSync[];
  reports: VnnicCorrectionReport[];
}

// ---------------------------------------------------------------------------
// RDAP (market.rs: /rdap/overview, /rdap/preview)

export type PublicationDisposition = 'PUBLISH' | 'REDACT' | 'OMIT' | 'CONTACT_FORM' | 'PUBLISH_IF_CONSENTED';

export interface RdapTld {
  zone: string;
  tld_class: string;
  icann_governed: boolean;
  rdap_enabled: boolean;
  whois_enabled: boolean;
  records: number;
  servable: number;
  last_projected_at: ISODateTime | null;
  /** Domains whose projection is missing or behind the domain / TLD settings. */
  pending_refresh: number;
}

export interface PublicationPolicy extends PolicyRef {
  in_force: boolean;
  match: Record<string, unknown>;
  redaction_text: string | null;
  fields: Record<string, PublicationDisposition>;
}

export interface RdapOverview {
  tlds: RdapTld[];
  publication_policies: PublicationPolicy[];
  /** Every publishable field, in RDAP order. */
  fields: string[];
}

export interface RdapPreviewField {
  field: string;
  shown: 'PUBLIC' | 'REDACTED' | 'CONTACT_FORM' | 'OMITTED';
  /** Public value; always null for registrant fields (personal data is not echoed to staff here). */
  value: unknown;
  registrant: boolean;
}

export type RdapPreview =
  | { query: string; found: false; policy: null; fields: [] }
  | {
      query: string;
      found: true;
      ascii_name: string;
      unicode_name: string;
      policy: { policy_key: string; version: string } | null;
      redaction_text: string;
      last_update: ISODateTime;
      fields: RdapPreviewField[];
    };

// ---------------------------------------------------------------------------

export interface MarketApi {
  listAuctions(query: AuctionListQuery, opts?: CallOptions): Promise<ItemList<Auction>>;
  getAuction(id: string, opts?: CallOptions): Promise<AuctionDetail>;
  createAuction(req: NewAuctionRequest, opts?: WriteOptions): Promise<{ id: string; policy_version: string }>;
  scheduleDrop(req: NewDropRequest, opts?: WriteOptions): Promise<Pick<Drop, 'id' | 'domain_name' | 'expected_drop_at' | 'status'>>;
  cancelAuction(id: string, req: AuctionReasonRequest, opts?: WriteOptions): Promise<AuctionStatusChange>;
  overrideAuction(id: string, req: AuctionOverrideRequest, opts?: WriteOptions): Promise<AuctionOverrideResult>;
  listBackorders(query: BackorderListQuery, opts?: CallOptions): Promise<ItemList<Backorder>>;
  getBackorder(id: string, opts?: CallOptions): Promise<BackorderDetail>;
  listDrops(query: BackorderListQuery, opts?: CallOptions): Promise<ItemList<Drop>>;
  getDrop(id: string, opts?: CallOptions): Promise<DropDetail>;
  listEscrowDeposits(query: EscrowDepositQuery, opts?: CallOptions): Promise<EscrowDepositList>;
  getEscrowDeposit(id: string, opts?: CallOptions): Promise<EscrowDepositDetail>;
  listLegalLocks(query: LegalLockQuery, opts?: CallOptions): Promise<LegalLockList>;
  getLegalLock(id: string, opts?: CallOptions): Promise<LegalLockDetail>;
  getCaseInbox(query: CaseInboxQuery, opts?: CallOptions): Promise<CaseInbox>;
  getVnnicCorrections(opts?: CallOptions): Promise<VnnicCorrections>;
  getRdapOverview(opts?: CallOptions): Promise<RdapOverview>;
  previewRdap(domain: string, opts?: CallOptions): Promise<RdapPreview>;
}
